import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { getSupabase, setCachedToken } from './supabase';
import { apiFetch, setUnauthenticatedHandler } from './api';
import { MODULES, type ModuleEntry } from '../config/nav';

/**
 * Session and permissions.
 *
 * Supabase holds the session; the Worker is the authority on who the staff
 * member is and what they may see. `/api/me` is fetched once per session and
 * gives the permission slugs used to filter the module list.
 */

export interface Me {
    readonly id: string;
    readonly email: string;
    readonly staffId: number;
    readonly staffName: string;
    readonly staffGroupId: number;
    readonly permissions: readonly string[];
    readonly isSuperAdmin: boolean;
}

export interface AuthState {
    readonly session: Session | null;
    readonly me: Me | null;
    /** True until the initial session check and profile fetch have settled. */
    readonly loading: boolean;
    readonly error: string | null;
    /** Modules this user may see, already filtered. */
    readonly modules: readonly ModuleEntry[];
    signIn: (email: string, password: string) => Promise<{ error: string | null }>;
    signOut: () => Promise<void>;
    can: (slug: string) => boolean;
}

/**
 * staff_groups.staff_group_id 2 — Employee.
 *
 * Field staff. 56 of the 59 staff are in this group, and in the legacy app a
 * `staff_level` 2 login landed on Backend_employeedashboard rather than the
 * dashboard. That routing is reproduced in App.tsx.
 *
 * Do NOT infer this from "holds no modules": the Employee role is seeded with
 * calendar, jobs, timesheet and notes (migration 20260916000011), so an
 * employee has four modules and that test never fires.
 */
export const EMPLOYEE_GROUP_ID = 2;

/** Exported so the dev-only design preview can supply a mock value. */
export const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
    const [session, setSession] = useState<Session | null>(null);
    const [me, setMe] = useState<Me | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Mirror the session into the synchronous token cache that apiFetch reads.
    useEffect(() => {
        let active = true;
        let unsubscribe: (() => void) | null = null;

        void (async () => {
            try {
                const sb = await getSupabase();
                const { data } = await sb.auth.getSession();
                if (!active) {
                    return;
                }
                setCachedToken(data.session?.access_token ?? null);
                setSession(data.session);
                if (data.session === null) {
                    setLoading(false);
                }

                const { data: sub } = sb.auth.onAuthStateChange((_event, next) => {
                    setCachedToken(next?.access_token ?? null);
                    setSession(next);
                    if (next === null) {
                        setMe(null);
                        setLoading(false);
                    }
                });
                unsubscribe = () => {
                    sub.subscription.unsubscribe();
                };
            } catch (err: unknown) {
                // Supabase could not be initialised at all — /config.json is
                // missing or malformed. Surface it and stop loading, rather than
                // leaving the app on a spinner forever with only an unhandled
                // rejection in the console to explain why.
                if (active) {
                    setError(
                        err instanceof Error
                            ? `Could not load configuration: ${err.message}`
                            : 'Could not load configuration',
                    );
                    setLoading(false);
                }
            }
        })();

        return () => {
            active = false;
            unsubscribe?.();
        };
    }, []);

    // Load the profile once there is a session.
    useEffect(() => {
        if (session === null) {
            return;
        }
        let active = true;
        setLoading(true);
        apiFetch<Me>('/me')
            .then((data) => {
                if (active) {
                    setMe(data);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (active) {
                    setMe(null);
                    setError(err instanceof Error ? err.message : String(err));
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });
        return () => {
            active = false;
        };
    }, [session]);

    const signIn = useCallback(async (email: string, password: string): Promise<{ error: string | null }> => {
        const sb = await getSupabase();
        const { error: signInError } = await sb.auth.signInWithPassword({ email, password });
        // Deliberately generic: distinguishing "no such user" from "wrong
        // password" tells an attacker which emails are valid.
        return { error: signInError === null ? null : 'Incorrect email or password. Please check and try again.' };
    }, []);

    const signOut = useCallback(async (): Promise<void> => {
        const sb = await getSupabase();
        await sb.auth.signOut();
        setCachedToken(null);
        setMe(null);
        setSession(null);
    }, []);

    // A 401 that survives a token refresh means the session is genuinely gone.
    useEffect(() => {
        setUnauthenticatedHandler(() => {
            setCachedToken(null);
            setSession(null);
            setMe(null);
            setLoading(false);
        });
        return () => {
            setUnauthenticatedHandler(null);
        };
    }, []);

    const can = useCallback(
        (slug: string): boolean => {
            if (me === null) {
                return false;
            }
            return me.isSuperAdmin || me.permissions.includes(slug);
        },
        [me],
    );

    const modules = useMemo(() => MODULES.filter((m) => can(m.slug)), [can]);

    const value = useMemo<AuthState>(
        () => ({ session, me, loading, error, modules, signIn, signOut, can }),
        [session, me, loading, error, modules, signIn, signOut, can],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
    const ctx = useContext(AuthContext);
    if (ctx === null) {
        throw new Error('useAuth must be used inside <AuthProvider>');
    }
    return ctx;
}
